import { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Loader2,
  Minus,
  RotateCcw,
  SendHorizontal,
  Sparkles,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { cn } from "@/lib/utils";
import { TextoMarkdown } from "../../misc/TextoMarkdown";
import { BotaoMicrofone } from "../voz/BotaoMicrofone";
import { useDitado } from "../voz/useDitado";
import {
  type AlvoPlaybook,
  enviarNoPlaybook,
  FALAS_RAPIDAS,
  falaDoCliente,
  lembrar,
  PEDIDO_INICIAL,
  TITULO,
} from "./playbook";

/**
 * A caixinha flutuante do playbook (ver playbook.ts). É um Dialog do Radix
 * NÃO modal: a página continua usável atrás, e — por ser Radix — funciona
 * também aberta de dentro de outro modal (o lead do CRM), que pausa o próprio
 * foco preso enquanto ela existe. Clicar fora não fecha; só o X.
 */

interface Mensagem {
  papel: "user" | "assistant";
  texto: string;
}

/** Só uma caixinha por vez: abrir outra fecha esta. */
const EVENTO_ABERTA = "north:playbook-aberto";

const carregarHistorico = async (
  conversationId: string,
): Promise<Mensagem[]> => {
  const { data, error } = await supabase
    .from("chat_messages")
    .select("role, content")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true })
    .limit(150);
  if (error) return [];
  return (data ?? [])
    .filter((m: any) => m.role === "user" || m.role === "assistant")
    .map((m: any) => ({ papel: m.role, texto: String(m.content ?? "") }));
};

export const CaixaPlaybook = ({
  alvo,
  aoFechar,
}: {
  alvo: AlvoPlaybook;
  aoFechar: () => void;
}) => {
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [conversa, setConversa] = useState<string | undefined>(undefined);
  const [pensando, setPensando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [minimizada, setMinimizada] = useState(false);
  const fimRef = useRef<HTMLDivElement>(null);
  const campoRef = useRef<HTMLTextAreaElement>(null);
  const chave = `${alvo.tipo}:${alvo.id}`;

  const enviar = useCallback(
    async (mensagem: string, conversaAtual: string | undefined) => {
      setErro(null);
      setPensando(true);
      if (mensagem !== PEDIDO_INICIAL)
        setMensagens((m) => [...m, { papel: "user", texto: mensagem }]);
      try {
        const r = await enviarNoPlaybook(alvo, mensagem, conversaAtual);
        setConversa(r.conversationId);
        lembrar.gravar(alvo, r.conversationId);
        setMensagens((m) => [...m, { papel: "assistant", texto: r.answer }]);
      } catch (e) {
        setErro(e instanceof Error ? e.message : String(e));
      } finally {
        setPensando(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chave],
  );

  // Abrir: continua a conversa lembrada; sem ela, pede o playbook.
  useEffect(() => {
    let ativo = true;
    setMensagens([]);
    setErro(null);
    const lembrada = lembrar.ler(alvo);
    (async () => {
      const historico = lembrada ? await carregarHistorico(lembrada) : [];
      if (!ativo) return;
      if (historico.length > 0) {
        setConversa(lembrada);
        setMensagens(historico);
      } else {
        setConversa(undefined);
        void enviar(PEDIDO_INICIAL, undefined);
      }
    })();
    window.dispatchEvent(new CustomEvent(EVENTO_ABERTA, { detail: chave }));
    const outraAbriu = (e: Event) =>
      (e as CustomEvent).detail !== chave && aoFechar();
    window.addEventListener(EVENTO_ABERTA, outraAbriu);
    return () => {
      ativo = false;
      window.removeEventListener(EVENTO_ABERTA, outraAbriu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: "end" });
  }, [mensagens, pensando]);

  const mandar = (mensagem: string) => {
    const limpa = mensagem.trim();
    if (!limpa || pensando) return;
    // Microfone ligado ou transcrição final no ar: vai o que está no campo.
    if (ditado.ouvindo || ditado.transcrevendo) ditado.descartar();
    setTexto("");
    void enviar(limpa, conversa);
  };

  // Falar em vez de digitar: o texto aparece no campo e vai ao parar.
  const ditado = useDitado({ aoMudarTexto: setTexto });

  const refazer = () => {
    if (pensando) return;
    lembrar.esquecer(alvo);
    setConversa(undefined);
    setMensagens([]);
    void enviar(PEDIDO_INICIAL, undefined);
  };

  const visiveis = mensagens.filter(
    (m) => !(m.papel === "user" && m.texto === PEDIDO_INICIAL),
  );

  return (
    <DialogPrimitive.Root
      open
      modal={false}
      onOpenChange={(aberto) => !aberto && aoFechar()}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            campoRef.current?.focus();
          }}
          data-playbook
          className={cn(
            "fixed bottom-4 right-4 z-[60] flex w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden",
            "rounded-lg border bg-card text-card-foreground shadow-2xl",
            minimizada ? "h-auto" : "h-[min(640px,calc(100vh-2rem))]",
          )}
        >
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <Sparkles className="h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-sm font-semibold">
                {TITULO[alvo.tipo]}
              </DialogPrimitive.Title>
              {alvo.nome && (
                <p className="truncate text-xs text-muted-foreground">
                  {alvo.nome}
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={refazer}
              disabled={pensando}
              title="Refazer o playbook do zero"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setMinimizada((m) => !m)}
              title={minimizada ? "Abrir" : "Minimizar"}
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title="Fechar"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          {!minimizada && (
            <>
              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
                {visiveis.map((m, i) =>
                  m.papel === "assistant" ? (
                    <TextoMarkdown
                      key={i}
                      texto={m.texto}
                      className="space-y-1.5 leading-relaxed"
                    />
                  ) : (
                    <div
                      key={i}
                      className="ml-8 rounded-xl bg-primary/10 px-3 py-2 text-foreground"
                    >
                      {m.texto}
                    </div>
                  ),
                )}
                {pensando && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {visiveis.length === 0
                      ? "Montando o playbook com os dados do cliente…"
                      : "Pensando…"}
                  </div>
                )}
                {erro && (
                  <div className="rounded-lg bg-destructive/10 px-3 py-2 text-destructive">
                    Não foi possível concluir: {erro}
                    {visiveis.length === 0 && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 pl-1"
                        onClick={refazer}
                      >
                        Tentar de novo
                      </Button>
                    )}
                  </div>
                )}
                <div ref={fimRef} />
              </div>

              <div className="border-t px-3 pb-3 pt-2">
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {FALAS_RAPIDAS[alvo.tipo].map((f) => (
                    <button
                      key={f}
                      type="button"
                      disabled={pensando}
                      onClick={() => mandar(falaDoCliente(f))}
                      className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                    >
                      {f}
                    </button>
                  ))}
                </div>
                {ditado.erro && (
                  <p className="mb-2 text-xs text-destructive">{ditado.erro}</p>
                )}
                {ditado.transcrevendo && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    Ajustando o texto… Enter envia sem ajuste.
                  </p>
                )}
                <div className="flex items-end gap-2">
                  {ditado.suportado && (
                    <BotaoMicrofone
                      ouvindo={ditado.ouvindo}
                      transcrevendo={ditado.transcrevendo}
                      desabilitado={pensando}
                      aoAlternar={() =>
                        ditado.ouvindo ? ditado.parar() : ditado.iniciar(texto)
                      }
                    />
                  )}
                  <textarea
                    ref={campoRef}
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        mandar(texto);
                      }
                    }}
                    rows={2}
                    maxLength={4000}
                    placeholder={
                      ditado.transcrevendo
                        ? "Transcrevendo áudio…"
                        : ditado.ouvindo
                          ? "Gravando… clique no quadrado para encerrar"
                          : alvo.tipo === "contato"
                            ? "Resposta do cliente ou pedido de mensagem de WhatsApp…"
                            : "O que o cliente disse?"
                    }
                    className="min-h-[44px] flex-1 resize-none rounded-lg border bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <Button
                    type="button"
                    size="icon"
                    onClick={() => mandar(texto)}
                    disabled={!texto.trim() || pensando}
                    title="Enviar"
                  >
                    <SendHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
